import React, { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import HostLink from '../components/HostLink'
import Button from '../components/Button'
import { ErrorAlert, SuccessAlert } from '../components/Alert'
import { resendConfirmation } from '../api'
import { useDocumentTitle } from '../components/PublicLayout'

// Backend GET /api/confirm redirects here with ?status=<one of these>.
const CONTENT = {
  confirmed: {
    icon: '✓',
    tone: 'green',
    title: 'Email confirmed',
    body: "You're all set. Your Praenum trial is active and your first weekly digest will arrive next Tuesday morning.",
    showResend: false,
    next: 'continue',
  },
  already_confirmed: {
    icon: '✓',
    tone: 'green',
    title: 'Already confirmed',
    body: "This email was already confirmed, so there is nothing more to do. Your digest is on its way.",
    showResend: false,
    next: 'continue',
  },
  expired: {
    icon: '⏱',
    tone: 'amber',
    title: 'This link has expired',
    body: 'Confirmation links are valid for 48 hours. Enter your email below and we’ll send you a fresh one.',
    showResend: true,
    next: null,
  },
  invalid: {
    icon: '?',
    tone: 'amber',
    title: 'This link doesn’t look right',
    body: 'We couldn’t match this confirmation link. It may have already been used. Enter your email below to get a new one.',
    showResend: true,
    next: null,
  },
  error: {
    icon: '!',
    tone: 'red',
    title: 'Something went wrong',
    body: 'We hit an unexpected problem confirming your email. Please try again in a moment, or request a fresh link below.',
    showResend: true,
    next: null,
  },
}

const TONE_RING = {
  green: 'bg-green-100 text-green-700',
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-red-100 text-red-700',
}

export default function ConfirmResult() {
  useDocumentTitle('Email confirmation')
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const status = params.get('status') || 'invalid'
  const c = CONTENT[status] || CONTENT.invalid

  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sentMsg, setSentMsg] = useState('')
  const [errMsg, setErrMsg] = useState('')

  async function handleResend(e) {
    e.preventDefault()
    setErrMsg('')
    setSentMsg('')
    setLoading(true)
    try {
      const res = await resendConfirmation(email)
      setSentMsg(res.message || 'If that email needs confirmation, we’ve sent a new link.')
    } catch (err) {
      setErrMsg(err.message || 'Could not send the email. Please try again shortly.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
        <div
          className={`mx-auto mb-5 h-14 w-14 rounded-full flex items-center justify-center text-2xl font-bold ${TONE_RING[c.tone]}`}
          aria-hidden
        >
          {c.icon}
        </div>
        <h1 className="text-xl font-bold text-gray-900">{c.title}</h1>
        <p className="mt-2 text-sm text-gray-600 leading-relaxed">{c.body}</p>

        {c.next === 'continue' && (
          <div className="mt-6">
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Go to my dashboard
            </Button>
          </div>
        )}

        {c.showResend && (
          <form onSubmit={handleResend} className="mt-6 text-left">
            <label htmlFor="confirm-resend-email" className="block text-sm font-medium text-gray-700 mb-1">
              Your email
            </label>
            <input
              id="confirm-resend-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yournonprofit.org"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <div className="mt-3">
              <Button type="submit" loading={loading} className="w-full">
                Send a new confirmation link
              </Button>
            </div>
            <div className="mt-3 space-y-2">
              <SuccessAlert message={sentMsg} />
              <ErrorAlert message={errMsg} />
            </div>
          </form>
        )}

        <p className="mt-6 text-xs text-gray-500">
          <HostLink to="/" className="hover:text-brand-500">Back to Praenum</HostLink>
        </p>
      </div>
    </div>
  )
}
